import { useState } from "react";

import type { AIBaseline, AIProviderSummary, AISummary } from "../api/types";
import { useAISummary } from "../hooks/useAI";
import { AI_MIN_SETTLED, beatsMarket, enoughData, money, providerLabel, share } from "../lib/ai";
import { Empty, Note, Panel } from "./Panel";

const CONFIDENCE_STEPS = [0.5, 0.6, 0.7, 0.8, 0.9];

/**
 * Сравнение провайдеров ИИ-судьи (ADR-0038).
 *
 * # Что панель обязана показать, и в каком порядке
 *
 * 1. НАДЁЖНОСТЬ И СКОРОСТЬ. Доля ответов и задержка p50/p95 — первое, что
 *    отличает провайдеров ещё до любого исхода.
 * 2. КАЛИБРОВКА РЯДОМ С РЫНКОМ. Brier и log-loss по P(Up) — против середины
 *    стакана и физики замка НА ТЕХ ЖЕ запросах. Провайдер полезен, только если
 *    обходит саму цену рынка: иначе он пересказывает стакан.
 * 3. ТЕНЕВОЙ СЧЁТ. «Если бы послушали»: по цене снимка и по цене в момент
 *    ответа — разница между ними и есть цена задержки провайдера.
 *
 * Порог уверенности — переключатель, а не настройка трейдера: PnL обеих
 * сторон хранятся, и вердикт пересчитывается при любом пороге.
 *
 * # Чего на панели НЕТ
 *
 * Победителя до порога данных. Пока у каждого провайдера меньше
 * AI_MIN_SETTLED сведённых ответов, панель пишет «рано судить».
 */
export function AIComparePanel({ hours }: { hours: number }) {
  const [minConf, setMinConf] = useState(0.6);
  const q = useAISummary(hours, minConf);
  const s = q.data?.summary;

  return (
    <Panel
      title="ИИ-судья: сравнение провайдеров"
      badge="dry"
      subtitle={s ? subtitleOf(s) : undefined}
      actions={
        <label className="flex items-center gap-1 text-xs text-[var(--color-muted)]">
          порог уверенности
          <select
            value={minConf}
            onChange={(e) => setMinConf(Number(e.target.value))}
            className="rounded border border-[var(--color-border)] bg-transparent px-1"
          >
            {CONFIDENCE_STEPS.map((c) => (
              <option key={c} value={c}>
                {c.toFixed(1)}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {!s ? (
        <Empty>{q.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
      ) : s.asks === 0 ? (
        <Empty>запросов к ИИ-судье за окно ещё не было — кнопка на странице рынка</Empty>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-2 font-normal">провайдер</th>
                  <th className="pb-2 text-right font-normal">ответил</th>
                  <th className="pb-2 text-right font-normal">ошибки / таймауты / отказы</th>
                  <th className="pb-2 text-right font-normal">задержка p50 / p95</th>
                  <th className="pb-2 text-right font-normal">сведено</th>
                  <th className="pb-2 text-right font-normal">Brier</th>
                  <th className="pb-2 text-right font-normal">log-loss</th>
                  <th className="pb-2 text-right font-normal">сторона угадана</th>
                  <th className="pb-2 text-right font-normal">ставок / выиграно</th>
                  <th className="pb-2 text-right font-normal">счёт по снимку</th>
                  <th className="pb-2 text-right font-normal">по цене ответа</th>
                </tr>
              </thead>
              <tbody>
                {s.providers.map((p) => (
                  <ProviderRow key={p.provider} p={p} s={s} />
                ))}
                {s.baselines.map((b) => (
                  <BaselineRow key={b.name} b={b} />
                ))}
              </tbody>
            </table>
          </div>
          <Note>
            Brier и log-loss — по P(Up) против исхода окна, меньше — лучше. Строки «рынок» и
            «модель замка» посчитаны на тех же запросах: провайдер полезен, только если обходит
            цену рынка. «Счёт по снимку» — купили бы по плану на момент вопроса, «по цене
            ответа» — по стакану, когда пришёл ответ.
            {!enoughData(s) &&
              ` Рано судить: выводы — после ${AI_MIN_SETTLED} сведённых ответов у каждого провайдера.`}
          </Note>
        </>
      )}
    </Panel>
  );
}

function subtitleOf(s: AISummary): string {
  const parts = [`запросов ${s.asks}`, `снимков ${s.built}`];
  if (s.expired) parts.push(`опоздавших ${s.expired}`);
  if (s.rejected) parts.push(`отвергнуто ${s.rejected}`);
  if (s.both_ok) parts.push(`согласие ${share(s.agreed, s.both_ok)} из ${s.both_ok}`);
  return parts.join(" · ");
}

function ProviderRow({ p, s }: { p: AIProviderSummary; s: AISummary }) {
  const beats = beatsMarket(p, s);
  const brierTone =
    beats === null ? "" : beats ? "text-[var(--color-good)]" : "text-[var(--color-bad)]";
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1">{providerLabel(p.provider)}</td>
      <td className="py-1 text-right">
        {p.ok} из {p.asks} ({share(p.ok, p.asks)})
      </td>
      <td className="py-1 text-right">
        {p.errors} / {p.timeouts} / {p.refusals}
        {p.unknown_shape > 0 && ` · не разобрано ${p.unknown_shape}`}
      </td>
      <td className="py-1 text-right">
        {ms(p.latency_p50_ms)} / {ms(p.latency_p95_ms)}
      </td>
      <td className="py-1 text-right">{p.settled}</td>
      <td className={`py-1 text-right ${brierTone}`}>{num(p.brier)}</td>
      <td className="py-1 text-right">{num(p.log_loss)}</td>
      <td className="py-1 text-right">
        {p.action_hits} из {p.actions} ({share(p.action_hits, p.actions)})
      </td>
      <td className="py-1 text-right">
        {p.entered} / {p.won}
      </td>
      <td className="py-1 text-right">{money(p.pnl_snapshot)}</td>
      <td className="py-1 text-right">{money(p.pnl_answer)}</td>
    </tr>
  );
}

function BaselineRow({ b }: { b: AIBaseline }) {
  return (
    <tr className="border-t border-[var(--color-border)] text-[var(--color-muted)]">
      <td className="py-1">{b.name === "market_mid" ? "рынок (середина стакана)" : "модель замка"}</td>
      <td className="py-1 text-right">—</td>
      <td className="py-1 text-right">—</td>
      <td className="py-1 text-right">—</td>
      <td className="py-1 text-right">{b.settled}</td>
      <td className="py-1 text-right">{num(b.brier)}</td>
      <td className="py-1 text-right">{num(b.log_loss)}</td>
      <td className="py-1 text-right">—</td>
      <td className="py-1 text-right">—</td>
      <td className="py-1 text-right">—</td>
      <td className="py-1 text-right">—</td>
    </tr>
  );
}

function num(v: number | undefined): string {
  return v === undefined || v === null ? "—" : v.toFixed(4);
}

function ms(v: number | undefined): string {
  return v === undefined || v === null ? "—" : `${Math.round(v)} мс`;
}
